import './stile/token.css';
import './stile/base.css';
import './stile/componenti.css';
import './stile/viste.css';
import { Motore } from './scena/motore';
import { creaRouter } from './core/router';
import { h } from './core/dom';
import { indirizzo, LINGUE, risolvi, t, dizionario, type Lingua, type Sezione, traduciPercorso } from './i18n';
import { ricaricaSeVecchio, versioneSuperata } from './core/ricarica';

const canvas = document.getElementById('scena') as HTMLCanvasElement;
const app = document.getElementById('app') as HTMLElement;
const vista = document.getElementById('vista') as HTMLElement;
const dock = document.getElementById('dock') as HTMLElement;
const lente = document.getElementById('dock-lente') as HTMLElement;
const orologio = document.getElementById('orologio') as HTMLElement;

// Windows (e quindi Chrome ed Edge lì) non disegna le bandiere emoji: al posto di 🇺🇸 mostra «US». Se il sistema
// disegna le emoji ma non le bandiere si attiva il carattere «Bandiere» (base.css). Stesso controllo di
// country-flag-emoji-polyfill: un pixel di testo nero e bianco uguale = glifo a colori.
function aColori(testo: string): boolean {
  const c = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
  if (!c) return true;
  c.canvas.width = c.canvas.height = 1;
  c.textBaseline = 'top';
  c.font = '100px "Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji",sans-serif';
  c.scale(0.01, 0.01);
  const pixel = (colore: string) => { c.clearRect(0, 0, 100, 100); c.fillStyle = colore; c.fillText(testo, 0, 0); return c.getImageData(0, 0, 1, 1).data.join(','); };
  const bianco = pixel('#fff'), nero = pixel('#000');
  return nero === bianco && !nero.startsWith('0,0,0,');
}
try {
  if (aColori('\u{1F60A}') && !aColori('\u{1F1E8}\u{1F1ED}')) document.documentElement.classList.add('senza-bandiere');
} catch { /* niente canvas: si resta come prima */ }

function webgl2(): boolean {
  try { return Boolean(document.createElement('canvas').getContext('webgl2')); } catch { return false; }
}

let motore: Motore | null = null;
// La classe «vetro-gl» (vetro disegnato dallo shader) la aggiunge il motore al primo fotogramma.
if (webgl2()) {
  try {
    motore = new Motore(canvas, app);
  } catch (e) {
    console.error('ArkOrb: 3D non disponibile', e);
    document.documentElement.classList.remove('vetro-gl');
  }
}

if (import.meta.env.DEV) (window as unknown as { motore: Motore | null }).motore = motore;

const router = creaRouter(vista, motore);

// ── Dock: la «lente» scivola sotto la voce attiva come una goccia ─────────
// Il bordo che guida corre più veloce di quello che segue: la lente si allunga e poi si ricompone.
const bordi = { s: 0, d: 0, obS: 0, obD: 0, pronto: false };
function posizionaLente(): void {
  const attiva = dock.querySelector<HTMLElement>('a[aria-current="page"]');
  if (!attiva) { bordi.obS = bordi.obD = bordi.s = bordi.d = 0; return; }
  bordi.obS = attiva.offsetLeft;
  bordi.obD = attiva.offsetLeft + attiva.offsetWidth;
  if (!bordi.pronto) { bordi.s = bordi.obS; bordi.d = bordi.obD; bordi.pronto = true; }
}
let ultimo = performance.now();
function animaLente(ora: number): void {
  const dt = Math.min((ora - ultimo) / 1000, 0.05);
  ultimo = ora;
  const destra = bordi.obS > bordi.s;
  const veloce = 1 - Math.exp(-dt * 16);
  const lento = 1 - Math.exp(-dt * 7);
  bordi.s += (bordi.obS - bordi.s) * (destra ? lento : veloce);
  bordi.d += (bordi.obD - bordi.d) * (destra ? veloce : lento);
  lente.style.setProperty('transform', `translateX(${bordi.s.toFixed(2)}px)`);
  lente.style.setProperty('width', `${Math.max(0, bordi.d - bordi.s).toFixed(2)}px`);
  requestAnimationFrame(animaLente);
}
requestAnimationFrame(animaLente);
window.addEventListener('resize', posizionaLente);

// ── Selettore della lingua (in alto a destra): porta alla stessa sezione nell'altra lingua, con la stessa query ──
// Un pulsante con la lingua corrente che apre il menu delle lingue, ciascuna col suo nome («Deutsch», «Español»):
// con cinque lingue i codici affiancati non stanno più nella testata del telefono.
const selettoreLingua = document.createElement('nav');
selettoreLingua.className = 'lingue';
const linguaAttuale = h('button', { type: 'button', class: 'lingue-attuale', 'aria-haspopup': 'true', 'aria-expanded': 'false' });
const menuLingue = h('div', { class: 'lingue-menu', hidden: true });
function apriLingue(aperto: boolean): void {
  menuLingue.hidden = !aperto;
  linguaAttuale.setAttribute('aria-expanded', String(aperto));
}
linguaAttuale.addEventListener('click', () => apriLingue(menuLingue.hidden));
document.addEventListener('click', (e) => { if (!selettoreLingua.contains(e.target as Node)) apriLingue(false); });
document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !menuLingue.hidden) { apriLingue(false); linguaAttuale.focus(); } });
for (const l of LINGUE) {
  const a = document.createElement('a');
  a.dataset.lingua = l;
  a.hreflang = l;
  a.lang = l;
  a.textContent = dizionario(l).meta.nome;
  a.addEventListener('click', (e) => {
    e.preventDefault();
    apriLingue(false);
    ricordaLingua(l);
    const qui = risolvi(location.pathname);
    router.vai((qui ? traduciPercorso(qui, l) : indirizzo('esplora', l)) + location.search + location.hash);
  });
  menuLingue.append(a);
}
selettoreLingua.append(linguaAttuale, menuLingue);
const testata = document.querySelector<HTMLElement>('.testata')!;
testata.append(selettoreLingua);

// ── Sfumature ai bordi della vista che scorre: misurate su testata e dock reali (tacca e barra di sistema
// degli smartphone le spostano), così i contenuti non si leggono mai sotto al logo o dentro al dock. ──
function misuraSfumature(): void {
  const alto = testata.getBoundingClientRect().bottom;
  const basso = window.innerHeight - dock.getBoundingClientRect().top;
  const radice = document.documentElement.style;
  radice.setProperty('--sfuma-alto-0', `${Math.round(alto - 6)}px`);
  radice.setProperty('--sfuma-alto-1', `${Math.round(alto + 34)}px`);
  radice.setProperty('--sfuma-basso-0', `${Math.round(basso)}px`);
  radice.setProperty('--sfuma-basso-1', `${Math.round(basso + 80)}px`);
}
misuraSfumature();
window.addEventListener('resize', misuraSfumature);
document.fonts?.ready.then(misuraSfumature);

const marchio = document.querySelector<HTMLAnchorElement>('.marchio')!;
const salta = document.querySelector<HTMLAnchorElement>('.salta')!;
salta.addEventListener('click', (e) => { e.preventDefault(); document.getElementById('vista')?.focus(); });
router.onCambio = (c) => {
  const d = t();
  dock.setAttribute('aria-label', d.dock.sezioni);
  selettoreLingua.setAttribute('aria-label', d.dock.lingua);
  linguaAttuale.textContent = d.meta.breve;
  linguaAttuale.setAttribute('aria-label', `${d.dock.lingua}: ${d.meta.nome}`);
  marchio.href = indirizzo('esplora');
  salta.textContent = d.comune.saltaContenuti;
  marchio.setAttribute('aria-label', d.dock.home);
  for (const a of dock.querySelectorAll<HTMLAnchorElement>('a[data-sezione]')) {
    const sezione = a.dataset.sezione as Sezione;
    a.href = indirizzo(sezione);
    a.querySelector('span')!.textContent = d.dock[sezione as 'esplora' | 'lanci' | 'classifica' | 'potenze' | 'storia' | 'eventi'];
    if (sezione === c.sezione) a.setAttribute('aria-current', 'page');
    else a.removeAttribute('aria-current');
  }
  for (const a of selettoreLingua.querySelectorAll<HTMLAnchorElement>('a')) {
    const l = a.dataset.lingua as typeof c.lingua;
    a.href = indirizzo(c.sezione, l);
    if (l === c.lingua) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
  }
  if (c.linguaCambiata) bordi.pronto = false;
  requestAnimationFrame(posizionaLente);
};

// ── Orologio: il tempo della scena (UTC), con l'accelerazione se attiva ──
// Data e ora in due parti: sugli schermi stretti la data si nasconde (CSS) e resta l'ora su una riga.
const orologioData = document.createElement('span');
const orologioOra = document.createElement('span');
orologioData.className = 'orologio-data';
orologio.append(orologioData, orologioOra);
const formatiUtc = new Map<string, [Intl.DateTimeFormat, Intl.DateTimeFormat]>();
setInterval(() => {
  const d = t();
  let f = formatiUtc.get(d.meta.locale);
  if (!f) {
    f = [
      new Intl.DateTimeFormat(d.meta.locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }),
      new Intl.DateTimeFormat(d.meta.locale, { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC' }),
    ];
    formatiUtc.set(d.meta.locale, f);
  }
  const tempo = motore?.adesso ?? Date.now();
  const v = motore?.velocita ?? 1;
  const differita = Math.abs(tempo - Date.now()) > 5000;
  orologioData.textContent = `${f[0].format(tempo)}, `;
  // Sul telefono la data si nasconde solo in diretta: nel tempo simulato è l'informazione principale.
  orologio.classList.toggle('simulato', differita || v !== 1);
  const passo = v === 2_592_000 ? d.esplora.meseSec : v === 86_400 ? d.esplora.giornoSec : `×${v}`;
  orologioOra.textContent = `${f[1].format(tempo)} UTC · ${v !== 1 ? passo : differita ? d.comune.simulato : d.comune.ora}`;
}, 250);

// ── Lingua: la scelta esplicita (selettore EN/IT) si ricorda; altrimenti vale la lingua principale del browser.
// Non l'indirizzo IP: un italiano all'estero vuole l'italiano, un turista in Italia la sua lingua. I motori di ricerca
// restano sull'indirizzo che chiedono (ogni lingua ha il suo, con hreflang). ──
const CHIAVE_LINGUA = 'arkorb-lingua';
function ricordaLingua(valore: Lingua): void {
  try { localStorage.setItem(CHIAVE_LINGUA, valore); } catch { /* navigazione privata: pazienza */ }
}
function linguaRicordata(): Lingua | null {
  try {
    const v = localStorage.getItem(CHIAVE_LINGUA);
    return (LINGUE as string[]).includes(v ?? '') ? (v as Lingua) : null;
  } catch { return null; }
}
function linguaDelBrowser(): Lingua | null {
  const l = (navigator.languages?.[0] ?? navigator.language ?? '').slice(0, 2).toLowerCase();
  return (LINGUE as string[]).includes(l) ? (l as Lingua) : null;
}
const ROBOT = /bot|crawl|spider|slurp|google|bing|yandex|baidu|duckduck|facebookexternalhit|lighthouse|headless/i;

// Statistiche (Cloudflare Web Analytics, senza cookie) solo per le persone: Googlebot esegue il sito con un vero Chrome
// e Cloudflare lo contava come visitatore (19-22 ott 2026: 63 «visite» su 79 erano il motore di ricerca).
if (import.meta.env.PROD && !ROBOT.test(navigator.userAgent) && location.hostname === 'arkorb.com') {
  const s = document.createElement('script');
  s.defer = true;
  s.src = 'https://static.cloudflareinsights.com/beacon.min.js';
  s.dataset.cfBeacon = JSON.stringify({ token: '66d0bf526ae84641a166893f8e1bc232' });
  document.head.append(s);
}

const iniziale = risolvi(location.pathname);
const robot = ROBOT.test(navigator.userAgent);
const voluta = robot ? null : linguaRicordata() ?? linguaDelBrowser();
if (iniziale && voluta && voluta !== iniziale.lingua) {
  history.replaceState(null, '', traduciPercorso(iniziale, voluta) + location.search + location.hash);
}
// Motori di ricerca su una pagina di dettaglio (lancio, razzo, base, azienda, paese, satellite): resta la pagina del
// Worker, che è già tutta lì. La sezione viva la coprirebbe (doppione della sezione) e, se l'elemento non è più nei
// dati, cambierebbe l'indirizzo («pagina con reindirizzamento»). Le persone vedono sempre il sito vivo.
if (robot && iniziale?.resto && vista.querySelector('.pagina-testo')) router.fissa(new URL(location.href));
else router.mostra(new URL(location.href));

// Telefono: la scheda resta aperta per giorni in background. Tornandoci dopo più di 20 minuti, se nel frattempo è
// stata pubblicata una versione nuova si ricarica (stesso indirizzo): niente sezioni mute né satelliti fermi.
let nascostaDal = 0;
document.addEventListener('visibilitychange', () => {
  if (document.hidden) { nascostaDal = Date.now(); return; }
  if (nascostaDal && Date.now() - nascostaDal > 20 * 60_000) {
    void versioneSuperata().then((vecchia) => { if (vecchia) ricaricaSeVecchio(); });
  }
});

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register('/sw.js').catch(() => { /* il sito funziona anche senza */ });
}
